import axios from "axios";
import { motion } from "framer-motion";
import { useEffect, useState, useMemo } from "react";
import { MdSupportAgent, MdChat } from "react-icons/md";
import { BASE_API_URL } from "../helpers/Config";
import toast from "react-hot-toast";
import { getPageStyles, MOTION } from "../helpers/pageTheme";

const SectionCard = ({ styles, children, className = "" }) => (
    <div className={`p-3 ${className}`} style={styles.card}>
        {children}
    </div>
);

const SupportScreen = ({ theme = "dark" }) => {
    const [activeFaq, setActiveFaq] = useState(null);
    const [SupportLinks, setSupportLinks] = useState([]);
    const isDark = theme === "dark";
    const styles = useMemo(() => getPageStyles(isDark), [isDark]);

    const FetchSupportDetails = async () => {
        try {
            const response = await axios.post(`${BASE_API_URL}/company/support`);
            const data = response?.data;
            if (data?.data) {
                const general = data?.data.find((item) => item.type === "general")?.link;
                const payment = data?.data.find((item) => item.type === "payment")?.link;
                const technical = data?.data.find((item) => item.type === "technical")?.link;
                const emergency = data?.data.find((item) => item.type === "emergency")?.link;
                setSupportLinks([
                    { name: "General Support", username: general, description: "For general inquiries and account issues", icon: "💬", responseTime: "Within 2 hours" },
                    { name: "Technical Support", username: technical, description: "For technical issues and bug reports", icon: "🔧", responseTime: "Within 4 hours" },
                    { name: "Billing Support", username: payment, description: "For payment and withdrawal issues", icon: "💰", responseTime: "Within 1 hour" },
                    { name: "Emergency Support", username: emergency, description: "For urgent security issues", icon: "🚨", responseTime: "Within 30 minutes" },
                ]);
            }
        } catch (error) {
            console.log(error);
            toast.error("Failed to load support details");
        }
    };

    useEffect(() => {
        FetchSupportDetails();
    }, []);

    const supportConditions = [
        "Support available 24/7 for all registered users",
        "Please have your User ID ready when contacting support",
        "Response time may vary during peak hours",
        "For security, never share your password with support staff",
        "Keep your conversation professional and respectful",
    ];

    const faqs = [
        { question: "What information should I provide when contacting support?", answer: "Please provide your User ID, the issue you're facing, steps to reproduce, and any relevant screenshots." },
        { question: "How long does it take to get a response?", answer: "Response times vary by support type. General inquiries: 2 hours, Technical: 4 hours, Billing: 1 hour, Emergency: 30 minutes." },
        { question: "Is Telegram support secure?", answer: "Yes, all our Telegram handlers are verified and encrypted. Never share sensitive information like passwords." },
        { question: "What languages are supported?", answer: "We currently support English, Spanish, and Chinese. More languages will be added soon." },
    ];

    return (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="px-3 py-3 overflow-auto" style={styles.page}>
            <motion.div variants={MOTION.container} initial="hidden" animate="visible" className="d-flex flex-column gap-3">
                <motion.div variants={MOTION.item} style={styles.hero()}>
                    <div className="d-flex align-items-center gap-2 mb-1">
                        <MdSupportAgent size={28} />
                        <h5 className="fw-bold mb-0">Support Center</h5>
                    </div>
                    <p className="mb-0 small opacity-75">Get help via Telegram — choose the right channel</p>
                </motion.div>

                <motion.div variants={MOTION.item}>
                    <p className="mb-2 px-1" style={styles.sectionTitle}>
                        <MdChat className="me-1" size={16} /> Telegram support
                    </p>
                    {SupportLinks.map((handler, index) => (
                        <SectionCard key={index} styles={styles} className={index < SupportLinks.length - 1 ? "mb-2" : ""}>
                            <div className="d-flex align-items-start gap-3">
                                <span style={{ fontSize: "1.5rem" }}>{handler.icon}</span>
                                <div className="flex-grow-1 min-w-0">
                                    <h6 className="fw-bold mb-1" style={{ fontSize: "0.9rem" }}>{handler.name}</h6>
                                    <p className="mb-1 fw-semibold" style={{ fontSize: "0.85rem", color: "#6366f1" }}>@{handler.username}</p>
                                    <p className="mb-2 small" style={{ color: styles.textMuted }}>{handler.description}</p>
                                    <small style={{ color: styles.textMuted }}>⏱ {handler.responseTime}</small>
                                </div>
                                <motion.button
                                    type="button"
                                    className="btn btn-sm flex-shrink-0"
                                    style={{ ...styles.submitBtn, padding: "8px 14px", fontSize: "0.75rem" }}
                                    whileTap={{ scale: 0.96 }}
                                    onClick={() => window.open(`https://t.me/${handler.username?.replace("@", "")}`, "_blank")}
                                >
                                    Contact
                                </motion.button>
                            </div>
                        </SectionCard>
                    ))}
                </motion.div>

                <motion.div variants={MOTION.item}>
                    <SectionCard styles={styles}>
                        <p className="mb-3" style={styles.sectionTitle}>Guidelines</p>
                        <ul className="list-unstyled mb-0">
                            {supportConditions.map((condition, index) => (
                                <li key={index} className="d-flex align-items-start gap-2 mb-2 small" style={{ color: styles.textMuted }}>
                                    <span style={{ color: "#22c55e" }}>✓</span>
                                    {condition}
                                </li>
                            ))}
                        </ul>
                    </SectionCard>
                </motion.div>

                <motion.div variants={MOTION.item}>
                    <SectionCard styles={styles} className="p-0 overflow-hidden">
                        <div className="px-3 py-3" style={{ borderBottom: `1px solid ${styles.listBorder}` }}>
                            <p className="mb-0" style={styles.sectionTitle}>FAQ</p>
                        </div>
                        {faqs.map((faq, index) => (
                            <div key={index} style={{ borderBottom: index < faqs.length - 1 ? `1px solid ${styles.listBorder}` : "none" }}>
                                <button
                                    type="button"
                                    className="btn w-100 text-start px-3 py-3 d-flex justify-content-between align-items-center"
                                    onClick={() => setActiveFaq(activeFaq === index ? null : index)}
                                    style={{ background: "none", border: "none", color: isDark ? "#f9fafb" : "#111827", fontSize: "0.85rem" }}
                                >
                                    <span className="fw-medium pe-2">{faq.question}</span>
                                    <motion.span animate={{ rotate: activeFaq === index ? 180 : 0 }}>▼</motion.span>
                                </button>
                                <motion.div initial={false} animate={{ height: activeFaq === index ? "auto" : 0, opacity: activeFaq === index ? 1 : 0 }} style={{ overflow: "hidden" }}>
                                    <p className="px-3 pb-3 mb-0 small" style={{ color: styles.textMuted }}>{faq.answer}</p>
                                </motion.div>
                            </div>
                        ))}
                    </SectionCard>
                </motion.div>

                <motion.div variants={MOTION.item}>
                    <div style={styles.notice} className="text-center">
                        <strong>Tip:</strong> Choose the right handler and include your User ID for faster help.
                    </div>
                </motion.div>
            </motion.div>
        </motion.div>
    );
};

export default SupportScreen;
